import { Component, HostBinding, Input, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { SkeletonModule } from 'primeng/skeleton';
import { TooltipModule } from 'primeng/tooltip';
import { DialogModule } from 'primeng/dialog';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Store } from '@ngxs/store';

import { AuthService } from '../../services/account/auth.service';
import { OnboardingService } from '../../services/onboarding/onboarding.service';
import { UtilityService } from '../../services/utility/utility.service';
import { MentorService } from '../../services/account/mentor.service';
import { ScholarshipsService } from '../../services/scholarships/scholarships.service';
import { StudentDirectApplicationService } from '../../services/student-direct-application/student-direct-application.service';

import { UserResponse } from '../../models/account/userResponse';
import { countryResponse } from '../../models/country/countryResponse';
import { StudentMentorResponse } from '../../models/account/mentor/studentMentorResponse';
import { MyScholarshipResponse } from '../../models/scholarships/myScholarshipResponse';

import { Gender, GenderDescriptions, MentorType } from '../../models/enums/enums';
import { CourseLevelEnum, CourseLevelEnumDescriptions } from '../../models/enums/courseLevelEnum';
import { EducationalLevelEnum } from '../../models/enums/EducationLevelEnum';
import { StudentMentorRequestStatus } from '../../models/enums/studentMentorRequestStatus';

import { ClarityCallPopupComponent } from '../../shared/components/clarity-call-popup/clarity-call-popup.component';
import { EducationInfoComponent } from '../application-form/sections/education-info/education-info.component';
import { TestScoresComponent } from '../application-form/sections/test-scores/test-scores.component';
import { RecommendationsComponent } from '../application-form/sections/recommendations/recommendations.component';
import { ExperiencesComponent } from '../application-form/sections/experiences/experiences.component';
import { AwardsAccoladesComponent } from '../application-form/sections/awards-accolades/awards-accolades.component';
import { EditPersonalDetailsComponent } from './edit-personal-details/edit-personal-details.component';

/** A document reference shown as a downloadable chip. */
interface ProfileDoc {
  name: string;
  url: string;
}

/** One education record, normalised for display. */
interface EducationView {
  id: number | null;
  levelLabel: string;
  schoolName: string;
  fromYear: number | null;
  toYear: number | null;
  city: string;
  country: string;
  boardName: string | null;
  subjects: { name: string; grade: string }[];
  totalGrade: string | null;
  doc: ProfileDoc | null;
}

/** One test-score tile, normalised for display. */
interface ScoreTile {
  key: string;
  label: string;
  score: string;
  outOf: string;
  doc: ProfileDoc | null;
}

/** One work / research experience, normalised for display. */
interface ExperienceView {
  id: number | null;
  designation: string;
  organisation: string;
  fromLabel: string;
  toLabel: string;
  duration: string;
  location: string;
  description: string;
  doc: ProfileDoc | null;
}

/** One recommender, normalised for display. */
interface RecommendationView {
  id: number | null;
  name: string;
  initials: string;
  organisation: string;
  email: string;
  contact: string;
  doc: ProfileDoc | null;
}

/** One award / accolade, normalised for display. */
interface AwardView {
  id: number | null;
  name: string;
  categoryLabel: string;
  levelLabel: string;
  issuer: string;
  description: string;
  doc: ProfileDoc | null;
}

/** One application card row for the sidebar. */
interface ApplicationView {
  id: number;
  instituteName: string;
  courseName: string;
  statusLabel: string;
}

/** A single checklist row in the profile-completion card. */
interface CompletionItem {
  label: string;
  done: boolean;
}

@Component({
  selector: 'app-view-profile',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    ButtonModule,
    SkeletonModule,
    TooltipModule,
    DialogModule,
    ClarityCallPopupComponent,
    EducationInfoComponent,
    TestScoresComponent,
    RecommendationsComponent,
    ExperiencesComponent,
    AwardsAccoladesComponent,
    EditPersonalDetailsComponent
  ],
  templateUrl: './view-profile.component.html',
  styleUrls: ['./view-profile.component.scss']
})
export class ViewProfileComponent implements OnInit {
  /** Profile owner id. When not supplied, it is read from the `:id` route param. */
  @Input() studentId: string | null = null;
  @Input() isMentorView: boolean = false;

  /**
   * Whether edit affordances are shown. When left null the component derives it —
   * a student may only edit their own profile. Other portals (mentor / operations /
   * school) embed this component and pass `[canEdit]="false"` explicitly.
   */
  @Input() canEdit: boolean | null = null;

  /** When hosted inside another page (e.g. operations student-details tab) drop the
   *  page background and the negative top-margin used to hug the student-portal navbar. */
  @Input()
  @HostBinding('class.vp-embedded')
  embedded = false;

  resolvedCanEdit = false;

  // ─── Edit dialogs ───────────────────────────────────────────────────────────
  // A throwaway host form the embedded application-form section components
  // register their own controls onto via `parentForm.setControl(...)`.
  hostForm: FormGroup;

  showPersonalDialog = false;
  showEducationDialog = false;
  showTestScoresDialog = false;
  showExperienceDialog = false;
  showRecommendationsDialog = false;
  showAwardsDialog = false;

  @ViewChild(TestScoresComponent) private testScoresComp?: TestScoresComponent;

  // ─── Header ────────────────────────────────────────────────────────────────
  user: UserResponse | null = null;
  loadingUser = false;

  // ─── Main-column sections ───────────────────────────────────────────────────
  educations: EducationView[] = [];
  loadingEducation = false;

  scoreTiles: ScoreTile[] = [];
  loadingTestScores = false;

  experiences: ExperienceView[] = [];
  loadingExperience = false;

  recommendations: RecommendationView[] = [];
  loadingRecommendations = false;

  awards: AwardView[] = [];
  loadingAwards = false;

  // ─── Sidebar ────────────────────────────────────────────────────────────────
  mentor: StudentMentorResponse | null = null;
  loadingMentor = false;

  applications: ApplicationView[] = [];
  loadingApplications = false;

  scholarships: MyScholarshipResponse[] = [];
  loadingScholarships = false;

  showClarityCall = false;

  // Lookup tables resolved once and reused across sections.
  private countries: countryResponse[] = [];
  private boardsById = new Map<number, string>();

  private readonly categoryLabels: Record<number, string> = {
    1: 'Sports', 2: 'Academic', 3: 'Leadership', 4: 'Social Work', 5: 'Competitions', 6: 'Others'
  };
  private readonly achievementLevelLabels: Record<number, string> = {
    1: 'School / Local', 2: 'State / Regional', 3: 'National', 4: 'International'
  };
  private readonly educationLevelLabels: Record<number, string> = {
    [EducationalLevelEnum.Grade10]: 'Grade 10',
    [EducationalLevelEnum.Grade11]: 'Grade 11',
    [EducationalLevelEnum.Grade12]: 'Grade 12',
    [EducationalLevelEnum.Bachelors]: 'Bachelors'
  };
  private readonly applicationStatusLabels = [
    'Pending Payment', 'Under Review', 'Applied', 'Sent to University', 'Application Completed',
    'Application Under Assessment', 'Conditional Offer', 'Rejected', 'Document Uploaded',
    'One Time Enrolment Fees Paid', 'Unconditional Offer', 'CAS - UK', 'Form I-20 - US',
    'CoE - Australia', 'LOA + PAL - Canada', 'Visa Support Letter - Ireland', 'Visa Applied',
    'Visa Approved', 'Visa Rejected', 'Insurance Paid', 'Accommodation Booked',
    'Assessing Loan Application', 'Loan Approved', 'Enrolled Ready to Fly'
  ];

  // Placeholder rows rendered while a section is loading.
  readonly skeletonRows = [1, 2];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private store: Store,
    private fb: FormBuilder,
    private authService: AuthService,
    private onboardingService: OnboardingService,
    private utilityService: UtilityService,
    private mentorService: MentorService,
    private scholarshipsService: ScholarshipsService,
    private studentDirectApplicationService: StudentDirectApplicationService
  ) {
    this.hostForm = this.fb.group({});
  }

  ngOnInit(): void {
    this.studentId = this.studentId || this.route.snapshot.paramMap.get('id') || this.getLoggedInUserId();
    this.resolveCanEdit();

    // Reference data first (countries + boards) so sections can resolve labels.
    this.loadReferenceData();

    this.loadUser();
    this.loadEducation();
    this.loadTestScores();
    this.loadExperience();
    this.loadRecommendations();
    this.loadAwards();
    this.loadMentor();
    this.loadApplications();
    this.loadScholarships();
  }

  // ─── canEdit resolution ─────────────────────────────────────────────────────

  private getLoggedInUserId(): string {
    try {
      const account = JSON.parse(localStorage.getItem('account') || '{}');
      return account.userId || account.id || account.Id || account.sub || '';
    } catch {
      return '';
    }
  }

  private resolveCanEdit(): void {
    if (this.canEdit !== null) {
      this.resolvedCanEdit = this.canEdit;
      return;
    }
    // Not explicitly set: a student can edit only their own profile.
    const loggedInId = this.getLoggedInUserId();
    this.resolvedCanEdit = !!loggedInId && !!this.studentId && loggedInId === this.studentId;
  }

  // ─── Reference data ─────────────────────────────────────────────────────────

  private loadReferenceData(): void {
    this.utilityService.getCountries().pipe(
      catchError(() => of({ result: [] as countryResponse[] } as any))
    ).subscribe(res => {
      this.countries = res?.result ?? [];
    });

    this.onboardingService.getBoards().pipe(
      catchError(() => of([]))
    ).subscribe(boards => {
      (boards || []).forEach(b => this.boardsById.set(b.id, b.name));
      // Re-resolve education board names once boards arrive.
      this.educations.forEach(e => {
        if (!e.boardName && (e as any)._boardId != null) {
          e.boardName = this.boardsById.get((e as any)._boardId) ?? e.boardName;
        }
      });
    });
  }

  private countryName(code: string | null | undefined): string {
    if (!code) return '';
    return this.countries.find(c => c.countryCode === code)?.name ?? code;
  }

  // ─── Header / personal details ──────────────────────────────────────────────

  private loadUser(): void {
    if (!this.studentId) return;
    this.loadingUser = true;
    this.authService.getUserByid(this.studentId).pipe(
      catchError(() => of(null))
    ).subscribe(res => {
      this.user = res;
      this.loadingUser = false;
    });
  }

  get fullName(): string {
    if (!this.user) return '';
    return `${this.user.firstName ?? ''} ${this.user.lastName ?? ''}`.trim();
  }

  get initials(): string {
    if (!this.user) return '';
    const f = this.user.firstName?.[0] ?? '';
    const l = this.user.lastName?.[0] ?? '';
    return (f + l).toUpperCase() || (this.user.firstName?.substring(0, 2) ?? '').toUpperCase();
  }

  get genderLabel(): string {
    return this.user?.gender ? GenderDescriptions[this.user.gender as Gender] ?? '' : '';
  }

  get homeCountryName(): string {
    return this.countryName(this.user?.homeCountry);
  }

  get intendedLevelLabel(): string {
    const lvl = this.user?.degreePreference;
    return lvl !== null && lvl !== undefined ? CourseLevelEnumDescriptions[lvl as CourseLevelEnum] ?? '' : '';
  }

  get intendedCountriesLabel(): string {
    const list = this.user?.destination ?? [];
    if (!list.length) return '';
    return this.countryName(list[0]);
  }

  get intendedCountriesExtra(): number {
    const list = this.user?.destination ?? [];
    return list.length > 1 ? list.length - 1 : 0;
  }

  get intendedCountriesTooltip(): string {
    return (this.user?.destination ?? []).map(c => this.countryName(c)).join(', ');
  }

  get hsGraduationYear(): string {
    const raw = this.user?.highSchoolEndDate || this.user?.graduatedOn;
    if (raw) return String(new Date(raw).getFullYear());
    // Fall back to the school-leaving year captured in the Education section.
    const g12 = this.educations.find(e => (e as any)._level === EducationalLevelEnum.Grade12);
    const source = g12 ?? this.educations.find(e => e.toYear != null);
    return source?.toYear ? String(source.toYear) : '';
  }

  formatDob(value: string | null | undefined): string {
    if (!value) return '';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
  }

  // ─── Education ──────────────────────────────────────────────────────────────

  loadEducation(): void {
    this.loadingEducation = true;
    this.onboardingService.getEducations(this.studentId ?? undefined).pipe(
      catchError(() => of(null))
    ).subscribe((res: any) => {
      const list = res?.result || res?.Result || [];
      this.educations = list.map((r: any) => this.mapEducation(r))
        .sort((a: EducationView, b: EducationView) => (a as any)._level - (b as any)._level);
      this.loadingEducation = false;
    });
  }

  private mapEducation(r: any): EducationView {
    const level = r.educationLevel ?? r.EducationLevel;
    const boardId = r.boardId ?? r.BoardId ?? null;
    const chips = (r.subjectAndGrades || r.SubjectAndGrades || []) as any[];
    const view: EducationView = {
      id: r.id ?? r.Id ?? null,
      levelLabel: this.educationLevelLabels[level] ?? (r.degreeName || r.DegreeName || 'Education'),
      schoolName: r.schoolName || r.SchoolName || '',
      fromYear: this.toYear(r.attendedFrom || r.AttendedFrom),
      toYear: this.toYear(r.attendedTo || r.AttendedTo),
      city: r.city || r.City || '',
      country: this.countryName(r.country || r.Country || ''),
      boardName: r.boardName || r.BoardName || (boardId != null ? this.boardsById.get(boardId) ?? null : null),
      subjects: chips.map(c => ({ name: c.SubjectName ?? c.subjectName ?? '', grade: c.Grade ?? c.grade ?? '' })),
      totalGrade: r.totalGrade ?? r.TotalGrade ?? null,
      doc: this.toDoc(r.documentUrl || r.DocumentUrl)
    };
    (view as any)._level = level ?? 0;
    (view as any)._boardId = boardId;
    return view;
  }

  private toYear(value: string | null | undefined): number | null {
    if (!value) return null;
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d.getFullYear();
  }

  // ─── Test scores ────────────────────────────────────────────────────────────

  loadTestScores(): void {
    if (!this.studentId) return;
    this.loadingTestScores = true;
    this.onboardingService.getTestScores(this.studentId).pipe(
      catchError(() => of(null))
    ).subscribe((res: any) => {
      const data = Array.isArray(res) ? res[0] : (res?.result?.[0] || res?.result || res);
      this.scoreTiles = data ? this.mapScoreTiles(data) : [];
      this.loadingTestScores = false;
    });
  }

  private mapScoreTiles(d: any): ScoreTile[] {
    const config: { key: string; label: string; outOf: string }[] = [
      { key: 'ieltsScores', label: 'IELTS', outOf: 'out of 9.0' },
      { key: 'toeflScores', label: 'TOEFL', outOf: 'out of 120' },
      { key: 'duolingoScores', label: 'Duolingo', outOf: 'out of 160' },
      { key: 'satScores', label: 'SAT', outOf: 'out of 1600' },
      { key: 'actScores', label: 'ACT', outOf: 'out of 36' },
      { key: 'greScores', label: 'GRE', outOf: 'out of 340' },
      { key: 'gmatScores', label: 'GMAT', outOf: 'out of 800' }
    ];
    const tiles: ScoreTile[] = [];
    for (const c of config) {
      const s = d[c.key];
      if (s && s.score != null && s.score !== '' && Number(s.score) > 0) {
        tiles.push({
          key: c.key,
          label: c.label,
          score: String(s.score),
          outOf: c.outOf,
          doc: this.toDoc(s.documentUrl)
        });
      }
    }
    const apExams = (d.apExams || []) as any[];
    apExams.forEach((ap, i) => {
      if (ap?.subjectScore != null) {
        tiles.push({
          key: `ap-${i}`,
          label: `AP · ${ap.subjectName || 'Subject'}`,
          score: String(ap.subjectScore),
          outOf: 'out of 5',
          doc: this.toDoc(ap.documentUrl)
        });
      }
    });
    return tiles;
  }

  // ─── Experience ─────────────────────────────────────────────────────────────

  loadExperience(): void {
    this.loadingExperience = true;
    this.onboardingService.getExperiences(this.studentId ?? undefined).pipe(
      catchError(() => of(null))
    ).subscribe((res: any) => {
      const list = Array.isArray(res) ? res : (res?.result || []);
      this.experiences = list.map((e: any) => this.mapExperience(e));
      this.loadingExperience = false;
    });
  }

  private mapExperience(e: any): ExperienceView {
    const from = e.from ? new Date(e.from) : null;
    const to = e.to ? new Date(e.to) : null;
    const cityCountry = [e.city, this.countryName(e.country)].filter(Boolean).join(', ');
    return {
      id: e.id ?? e.Id ?? null,
      designation: e.designation || '',
      organisation: e.organisationName || '',
      fromLabel: this.formatMonthYear(from),
      toLabel: this.formatMonthYear(to),
      duration: this.durationLabel(from, to),
      location: cityCountry,
      description: e.keyResponsibilities || '',
      doc: this.toDoc(e.experienceOrCVLetterUrl)
    };
  }

  private formatMonthYear(d: Date | null): string {
    if (!d || isNaN(d.getTime())) return '';
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short' });
  }

  private durationLabel(from: Date | null, to: Date | null): string {
    if (!from || !to || isNaN(from.getTime()) || isNaN(to.getTime())) return '';
    let months = (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth());
    months = Math.max(months, 0);
    if (months < 1) return '< 1 month';
    if (months < 12) return `${months} month${months > 1 ? 's' : ''}`;
    const years = Math.floor(months / 12);
    const rem = months % 12;
    return rem ? `${years} yr ${rem} mo` : `${years} year${years > 1 ? 's' : ''}`;
  }

  // ─── Recommendations ────────────────────────────────────────────────────────

  loadRecommendations(): void {
    this.loadingRecommendations = true;
    this.onboardingService.getRecommendations(this.studentId ?? undefined).pipe(
      catchError(() => of(null))
    ).subscribe((res: any) => {
      const list = Array.isArray(res) ? res : (res?.result || []);
      this.recommendations = list.map((r: any) => this.mapRecommendation(r));
      this.loadingRecommendations = false;
    });
  }

  private mapRecommendation(r: any): RecommendationView {
    const name = r.name || '';
    return {
      id: r.id ?? r.Id ?? null,
      name,
      initials: this.nameInitials(name),
      organisation: r.organisationName || '',
      email: r.officialEmail || '',
      contact: r.contactNumber || '',
      doc: this.toDoc(r.lorUrl || r.lobUrl)
    };
  }

  // ─── Awards ─────────────────────────────────────────────────────────────────

  loadAwards(): void {
    this.loadingAwards = true;
    this.onboardingService.getAwards(this.studentId ?? undefined).pipe(
      catchError(() => of(null))
    ).subscribe((res: any) => {
      const list = Array.isArray(res) ? res : (res?.result || []);
      this.awards = list.map((a: any) => this.mapAward(a));
      this.loadingAwards = false;
    });
  }

  private mapAward(a: any): AwardView {
    return {
      id: a.id ?? a.Id ?? null,
      name: a.awardName || a.AwardName || '',
      categoryLabel: this.categoryLabels[a.achievementType ?? a.AchievementType] ?? '',
      levelLabel: this.achievementLevelLabels[a.achievementLevel ?? a.AchievementLevel] ?? '',
      issuer: a.organisationOrCause || a.OrganisationOrCause || a.issuingBody || a.IssuingBody || '',
      description: a.description || a.Description || '',
      doc: this.toDoc(a.documentUrl || a.DocumentUrl)
    };
  }

  // ─── Sidebar: mentor ────────────────────────────────────────────────────────

  private loadMentor(): void {
    this.loadingMentor = true;
    this.mentorService.getStudentMentors(MentorType.Mentor, this.studentId ?? undefined).pipe(
      catchError(() => of({ result: [] } as any))
    ).subscribe((res: any) => {
      const all: StudentMentorResponse[] = res?.result ?? [];
      this.mentor = all.find(m => m.status === StudentMentorRequestStatus.Approved) ?? all[0] ?? null;
      this.loadingMentor = false;
    });
  }

  get mentorName(): string {
    if (!this.mentor) return '';
    return `${this.mentor.firstName ?? ''} ${this.mentor.lastName ?? ''}`.trim();
  }

  get mentorInitials(): string {
    return this.nameInitials(this.mentorName);
  }

  get mentorSubtitle(): string {
    if (!this.mentor) return '';
    const parts: string[] = [];
    if (this.mentor.mastersCollageName) parts.push(this.mentor.mastersCollageName);
    else if (this.mentor.graduationCollageName) parts.push(this.mentor.graduationCollageName);
    return parts.join(' · ');
  }

  // ─── Sidebar: applications ──────────────────────────────────────────────────

  private loadApplications(): void {
    if (!this.studentId) return;
    this.loadingApplications = true;
    this.studentDirectApplicationService.getByStudentId({ studentId: this.studentId }).pipe(
      catchError(() => of(null))
    ).subscribe((res: any) => {
      const list: any[] = Array.isArray(res) ? res : (res?.result ?? []);
      this.applications = list.slice(0, 4).map(a => ({
        id: a.id,
        instituteName: a.instituteName || a.instituteId || 'University',
        courseName: a.courseName || a.courseLevelName || '',
        statusLabel: this.applicationStatusLabels[a.status] ?? ''
      }));
      this.loadingApplications = false;
    });
  }

  // ─── Sidebar: scholarships ──────────────────────────────────────────────────

  private loadScholarships(): void {
    this.loadingScholarships = true;
    this.scholarshipsService.getMyScholarships(this.studentId ?? undefined).pipe(
      catchError(() => of({ result: [] } as any))
    ).subscribe((res: any) => {
      this.scholarships = (res?.result ?? []).slice(0, 4);
      this.loadingScholarships = false;
    });
  }

  scholarshipDeadline(s: MyScholarshipResponse): string {
    const dl: any = s?.scholarship?.deadLine;
    if (!dl) return '';
    const raw = typeof dl === 'string' ? dl : (dl.date || dl.deadline || dl.value || '');
    if (!raw) return '';
    const d = new Date(raw);
    return isNaN(d.getTime()) ? String(raw) : `Deadline ${d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}`;
  }

  // ─── Profile completion ─────────────────────────────────────────────────────

  get hasPersonalDetails(): boolean {
    return !!(this.user && this.user.firstName && (this.user.bornOn || this.user.gender || this.user.homeCountry));
  }

  // get completionItems(): CompletionItem[] {
  //   return [
  //     { label: 'Personal Details', done: this.hasPersonalDetails },
  //     { label: 'Education', done: this.educations.length > 0 },
  //     { label: 'Recommendations', done: this.recommendations.length > 0 },
  //     { label: 'Awards & Accolades', done: this.awards.length > 0 },
  //     { label: 'Test Scores', done: this.scoreTiles.length > 0 },
  //     { label: 'Experience', done: this.experiences.length > 0 }
  //   ];
  // }
get completionItems(): CompletionItem[] {
  return [
    { label: 'Personal Details',   done: this.hasPersonalDetails },
    { label: 'Education',          done: this.educations.length > 0 },
    { label: 'Test Scores',        done: this.scoreTiles.length > 0 },
    { label: 'Experience',         done: this.experiences.length > 0 },
    { label: 'Recommendations',    done: this.recommendations.length > 0 },
    { label: 'Awards & Accolades', done: this.awards.length > 0 }
  ];
}
  get completedCount(): number {
    return this.completionItems.filter(i => i.done).length;
  }

  get completionPercentage(): number {
    const items = this.completionItems;
    return items.length ? Math.round((this.completedCount / items.length) * 100) : 0;
  }

  /** Circumference-based dash offset for the SVG progress ring (r = 42). */
  get ringDashOffset(): number {
    const circumference = 2 * Math.PI * 42;
    return circumference * (1 - this.completionPercentage / 100);
  }
  readonly ringCircumference = 2 * Math.PI * 42;

  // ─── Shared helpers ─────────────────────────────────────────────────────────

  private nameInitials(name: string): string {
    if (!name) return '';
    const parts = name.trim().split(/\s+/);
    if (parts.length > 1) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return name.substring(0, 2).toUpperCase();
  }

  private toDoc(url: string | null | undefined): ProfileDoc | null {
    if (!url || typeof url !== 'string') return null;
    const clean = url.split('?')[0];
    const name = decodeURIComponent(clean.split('/').pop() || 'Document');
    return { name, url };
  }

  openDoc(doc: ProfileDoc | null): void {
    if (doc?.url) window.open(doc.url, '_blank');
  }

  // ─── Edit dialogs (only meaningful when canEdit) ─────────────────────────────

  openPersonalDialog(): void { this.showPersonalDialog = true; }
  openEducationDialog(): void { this.showEducationDialog = true; }
  openTestScoresDialog(): void { this.showTestScoresDialog = true; }
  openExperienceDialog(): void { this.showExperienceDialog = true; }
  openRecommendationsDialog(): void { this.showRecommendationsDialog = true; }
  openAwardsDialog(): void { this.showAwardsDialog = true; }

  /** Personal-details form saved — refresh header, close. */
  onPersonalSaved(): void {
    this.showPersonalDialog = false;
    this.loadUser();
  }

  /** Profile photo uploaded from the edit dialog — reflect it immediately in the header. */
  onPhotoUpdated(url: string): void {
    if (this.user) this.user.imageUrl = url;
  }

  /** Test-scores has no inline save button — trigger its save from the footer. */
  saveTestScores(): void {
    this.testScoresComp?.saveData();
  }

  onTestScoresSaved(): void {
    this.loadTestScores();
    this.showTestScoresDialog = false;
  }

  onCompleteProfile(): void {
    this.router.navigate(['portal', 'application-form']);
  }

  onDirectApplication(): void {
    this.router.navigate(['portal', 'da-forms']);
  }

  onScheduleClarityCall(): void {
    this.showClarityCall = true;
  }

  onFindMentor(): void {
    this.router.navigate(['portal', 'mentor-me']);
  }

  onMessageMentor(): void {
    this.router.navigate(['portal', 'chat']);
  }

  onViewApplications(): void {
    this.router.navigate(['portal', 'my-institutes']);
  }

  onBrowseColleges(): void {
    this.router.navigate(['portal', 'institute-search']);
  }

  onBrowseScholarships(): void {
    this.router.navigate(['portal', 'scholarships']);
  }
}
